import React, { useState, useEffect } from 'react';
import {
  TextInput,
  Stack,
  Button,
  Paper,
  Title,
  Group,
  Checkbox,
  LoadingOverlay,
  Text,
  Grid,
  Select
} from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { useApp } from '../../context/AppContext';
import { convertTo24HourFormat, convertTo12HourFormat } from '../../utils/timeUtils';
import PhoneInput from '../common/PhoneInput';
import { useSalon } from '../../context/SalonContext';
import { useMediaQuery } from '@mantine/hooks';

function EmployeeForm({ 
  onSubmit, 
  onClose, 
  initialData, 
  showOnlyHours = false
}) {
  const { inviteStaff, updateStaff } = useApp();
  const { currentUser, businessInfo } = useSalon();
  const [isLoading, setIsLoading] = useState(false);
  const [selectableRoles, setSelectableRoles] = useState([]);
  const isMobile = useMediaQuery('(max-width: 450px)');
  const days = [
    { key: 'sunday', label: 'Sunday' },
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
  ];
  
  const convertBusinessHoursToSchedule = (data, isInitialData = true) => {
    if (!data) {
      return {
        'Sunday': { enabled: true, start: null, end: null },
        'Monday': { enabled: true, start: null, end: null },
        'Tuesday': { enabled: true, start: null, end: null },
        'Wednesday': { enabled: true, start: null, end: null },
        'Thursday': { enabled: true, start: null, end: null },
        'Friday': { enabled: true, start: null, end: null },
        'Saturday': { enabled: true, start: null, end: null }
      };
    }

    return days.reduce((schedule, { key }) => {
      const start_key = isInitialData ? `${key}_start_time` : `${key}_open_time`;
      const end_key = `${key}_end_time`;
      return {
        ...schedule,
        [`${key}_start_time`]: convertTo24HourFormat(data[start_key]),
        [`${key}_end_time`]: convertTo24HourFormat(data[end_key])
      };
    }, {});
  };

  const scrollToTop = () => {
    document.querySelector('.mantine-Modal-content')?.scrollTo({ top: 0 });
  }

  useEffect(() => {
    const selectableRoles = [];
    if (currentUser?.role === "Owner") {
      selectableRoles.push({ value: 'Owner', label: 'Owner' });
    }
    
    if (currentUser?.role === "Manager" || currentUser?.role === "Owner") {
      selectableRoles.push({ value: 'Manager', label: 'Manager' });
    }

    selectableRoles.push({ value: 'Employee', label: 'Employee' });
    setSelectableRoles(selectableRoles);
  }, [currentUser]);

  useEffect(() => {
    const newBusinessHours = initialData ? convertBusinessHoursToSchedule(initialData) : convertBusinessHoursToSchedule(businessInfo, false);
    
    // Format phone number from area_code and phone_number
    const formattedPhone = initialData?.phone_number ? 
      `(${initialData.area_code}) ${initialData.phone_number.slice(0,3)}-${initialData.phone_number.slice(3)}` : 
      '';

    setFormData({
      id: initialData?.id,
      firstName: initialData?.first_name || '',
      lastName: initialData?.last_name || '',
      email: initialData?.email || '',
      phone: formattedPhone,
      title: initialData?.title || '',
      role: initialData?.role || 'Employee',
      ...newBusinessHours
    });
  }, [businessInfo, initialData]);

  const [formData, setFormData] = useState({});

  const convertFormTimesToTwelveHourFormat = (data) => {
    return days.reduce((acc, { key }) => {
      return {
        ...acc,
        [`${key}_start_time`]: convertTo12HourFormat(data[`${key}_start_time`]),
        [`${key}_end_time`]: convertTo12HourFormat(data[`${key}_end_time`]),
      };
    }, {});
  };

  const splitPhoneNumber = (fullPhoneNumber) => {
    const cleanedPhoneNumber = fullPhoneNumber.replace(/\D/g, '');
    const areaCode = cleanedPhoneNumber.slice(0, 3); 
    const phoneNumber = cleanedPhoneNumber.slice(3);
    return { areaCode, phoneNumber };
  }

  const createEmployee = async () => {
    try {    // Parse phone number into area code and 7 digit number
      const fullPhoneNumber = formData.phone.replace(/\D/g, ''); // Remove non-digits
        
      const scheduleData = convertFormTimesToTwelveHourFormat(formData);

      const cognitoResponse = await inviteStaff({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone_number: fullPhoneNumber,
          title: formData.title,
          role: formData.role,
          salon_id: businessInfo.id,
          ...scheduleData,
        });

        if (cognitoResponse) {
          const { sub: employeeId } = cognitoResponse;

          const { areaCode, phoneNumber } = splitPhoneNumber(fullPhoneNumber);
          onSubmit({
            id: employeeId,
            first_name: cognitoResponse.given_name,
            last_name: cognitoResponse.family_name,
            email: cognitoResponse.email,
            country_code: "+1",
            area_code: areaCode,
            phone_number: phoneNumber,
            title: formData.title,
            role: formData.role,
            ...scheduleData,
          });
          onClose();
        }
      } catch (error) {
        setIsLoading(false);
        console.error('Error creating staff member:', error);
        // Handle error (show error message to user)
      }
  }

  const updateEmployee = async () => {
    try {
      const scheduleData = convertFormTimesToTwelveHourFormat(formData);
      const payload = {
        id: formData.id,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        title: formData.title,
        role: formData.role,
        ...scheduleData,
      }
      const response = await updateStaff(payload);

      if (response) {
        setIsLoading(false);
        onSubmit(payload);
        onClose();
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Error updating staff member:', error);
      // Handle error (show error message to user)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.title || !formData.role) {
      console.log("Invalid form data, missing required fields", formData);
      return;
    }

    setIsLoading(true);
    scrollToTop();
    if (!formData.id) {
      createEmployee();
    } else {
      updateEmployee();
    }
    
  };

  return (
    <Stack>
      {isLoading && <LoadingOverlay visible={true} overlayBlur={2} />}
      <Paper p={{base: "0", sm: "md"}}>
        {!showOnlyHours && (
          <Group position="apart" mb="md">
            <Title order={3}>Employee Information</Title>
          </Group>
        )}

        <form onSubmit={handleSubmit}>
          <Stack gap={isMobile ? "sm" : "md"}>
            {!showOnlyHours && (
              <>
                <Grid>
                  <Grid.Col span={isMobile ? 12 : 6}>
                    <TextInput
                      label="First Name"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    />
                  </Grid.Col>
                  <Grid.Col span={isMobile ? 12 : 6}>
                    <TextInput
                      label="Last Name"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    />
                  </Grid.Col>
                </Grid>

                <TextInput
                  label="Title"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Hair Stylist, Nail Technician"
                />

                <Select
                  label="Role"
                  required
                  value={formData.role}
                  onChange={(value) => setFormData({ ...formData, role: value })}
                  data={selectableRoles}
                  disabled={initialData?.isSelf}
                />

                <TextInput
                  label="Email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!!initialData} // No Email change for now, since we don't sync to cognito yet
                />

                <PhoneInput
                  required
                  value={formData.phone}
                  onChange={(value) => setFormData({ ...formData, phone: value })}
                  disabled={!!initialData}
                />
              </>
            )}

            <Title order={4} mt="md">Employee Working Hours</Title>
            
            {days.map(({ key, label }) => {
              const isEnabled = !!(formData[`${key}_start_time`] || formData[`${key}_end_time`]);
              
              return (
                <Grid key={key} align="center" gutter={40}>
                  <Grid.Col span={isMobile ? 12 : 3} p="md" mt="md" style={{ paddingBottom: isMobile ? "0" : "md" }}>
                    <Checkbox
                      label={label}
                      checked={isEnabled}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          [`${key}_start_time`]: e.currentTarget.checked ? `${convertTo24HourFormat(businessInfo[`${key}_open_time`])}` : null,
                          [`${key}_end_time`]: e.currentTarget.checked ? `${convertTo24HourFormat(businessInfo[`${key}_end_time`])}` : null,
                        });
                      }}
                    />
                  </Grid.Col>
                  {isEnabled ? (
                    <>
                      <Grid.Col span={isMobile ? 6 : 4} align="center" pr="md" pl={isMobile ? "xl" : "0"} py={0} mb={isMobile ? "md" : "0"} mt={isMobile ? "0" : "md"}>
                        <TimeInput
                          value={formData[`${key}_start_time`] || ''}
                          format="12"
                          withSeconds={false}
                          step={900}
                          onChange={(event) => {
                            setFormData({
                              ...formData,
                              [`${key}_start_time`]: event.currentTarget.value
                            });
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={isMobile ? 6 : 4} align="center" pl={0} pr={isMobile ? "xl" : "0"} py={0} mb={isMobile ? "md" : "0"} mt={isMobile ? "0" : "md"}>
                        <TimeInput
                          value={formData[`${key}_end_time`] || ''}
                          format="12"
                          withSeconds={false}
                          step={900}
                          onChange={(event) => {
                            setFormData({
                              ...formData,
                              [`${key}_end_time`]: event.currentTarget.value
                            });
                          }}
                        />
                      </Grid.Col>
                    </>
                  ) : (
                    <Grid.Col span={8} align="center" p={isMobile ? "0" : "md"} mt={isMobile ? "0" : "md"}>
                      { !isMobile && <Text>Unavailable</Text> }
                    </Grid.Col>
                  )}
                </Grid>
              );
            })}

            <Button type="submit" fullWidth mt="xl" color="violet">
              {showOnlyHours 
                ? 'Save Hours'
                : formData.id 
                  ? 'Save Changes' 
                  : 'Add New Employee'
              }
            </Button>
          </Stack>
        </form>
      </Paper>
    </Stack>
  );
}

export default EmployeeForm; 